import React, { useState, useRef, useEffect } from "react";
import Select from "react-select";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";

const AddEditAssetPopup = ({ asset, onClose, onSave, users, isEditMode }) => {
  const [assetName, setAssetName] = useState("");
  const [assetId, setAssetId] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(null);
  const [manufacturer, setManufacturer] = useState("");
  const [model, setModel] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [condition, setCondition] = useState("");
  const [warranty, setWarranty] = useState("");
  const [value, setValue] = useState("");
  const [assetUser, setAssetUser] = useState(null);
  const [status, setStatus] = useState(null);
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const submitButtonRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");

  const statusOptions = [
    { value: "Available", label: "Available" },
    { value: "Assigned", label: "Assigned" },
    { value: "In Maintenance", label: "In Maintenance" },
    { value: "Pending Approval", label: "Pending Approval" },
    { value: "Under Inspection", label: "Under Inspection" },
    { value: "Decommissioned", label: "Decommissioned" },
    { value: "Lost/Stolen", label: "Lost/Stolen" },
    { value: "Reserved", label: "Reserved" },
    { value: "Disposed", label: "Disposed" },
  ];

  const userOptions = users.map((user) => ({ value: user.id, label: `${user.name} ${user.lastname}` }));

  // Function to reset form fields
  const resetForm = () => {
    setAssetName("");
    setAssetId("");
    setPurchaseDate(null);
    setManufacturer("");
    setModel("");
    setSerialNumber("");
    setCondition("");
    setWarranty("");
    setValue("");
    setAssetUser(null);
    setStatus(null);
    setDescription("");
    setErrors({});
  };

  // Prefill form values in edit mode or reset form in add mode
  useEffect(() => {
    if (isEditMode && asset) {
      setAssetName(asset.asset_type || "");
      setAssetId(asset.asset_id || "");
      setPurchaseDate(asset.purchase_date ? new Date(asset.purchase_date) : null);
      setManufacturer(asset.manufacturer || "");
      setModel(asset.model || "");
      setSerialNumber(asset.serial_number || "");
      setCondition(asset.condition || "");
      setWarranty(asset.warranty_period || "");
      setValue(asset.value || "");
      setAssetUser(asset.assigned_to ? { value: asset.assigned_to, label: asset.assigned_to } : null);
      setStatus(asset.status ? { value: asset.status, label: asset.status } : null);
      setDescription(asset.description || "");
    } else {
      resetForm(); // Reset the form for add mode
    }
  }, [isEditMode, asset]);

  const validateForm = () => {
    const newErrors = {};
    if (!assetName) newErrors.assetName = "Asset name is required.";
    if (!assetId) newErrors.assetId = "Asset ID is required.";
    if (!purchaseDate) newErrors.purchaseDate = "Purchase date is required.";
    if (!manufacturer) newErrors.manufacturer = "Manufacturer is required.";
    if (!model) newErrors.model = "Model is required.";
    if (!serialNumber) newErrors.serialNumber = "Serial number is required.";
    if (!condition) newErrors.condition = "Condition is required.";
    if (!warranty) newErrors.warranty = "Warranty is required.";
    if (!value) newErrors.value = "Value is required.";
    if (!status) newErrors.status = "Status is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const assetData = {
      assetName,
      assetId,
      purchaseDate: purchaseDate ? purchaseDate.toISOString().split("T")[0] : null,
      manufacturer,
      model,
      serialNumber,
      condition,
      warranty,
      value,
      assetUser: assetUser ? assetUser.value : null,
      status: status ? status.value : null,
      description,
    };

    try {
      let response;
      if (isEditMode && asset) {
        response = await axios.put(`${apiUrl}/assets/asset/edit/${asset.id}`, assetData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Asset updated successfully");
      } else {
        response = await axios.post(`${apiUrl}/assets/asset`, assetData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Asset added successfully");
      }

      onSave(response.data);
      resetForm(); // Reset the form after successful submission

      if (submitButtonRef.current) submitButtonRef.current.click();
    } catch (error) {
      console.error("Error saving asset:", error);
      toast.error("Failed to save the asset. Please try again.");
    }
  };

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#ff9b44" : "#fff",
      color: state.isFocused ? "#fff" : "#000",
      "&:hover": {
        backgroundColor: "#ff9b44",
      },
    }),
  };


  return (
    <div id="add_edit_asset" className="modal custom-modal fade" role="dialog">
      <div className="modal-dialog modal-lg" style={{ margin: 0, position: "fixed", right: 0, height: "100vh", width: "70%" }}>
        <div className="modal-content h-100 d-flex flex-column">
          <form onSubmit={handleSubmit}>
            <div className="modal-header">
              <h5 className="modal-title">{isEditMode ? "Edit Asset" : "Add Asset"}</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={onClose}>
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body flex-grow-1 overflow-auto" style={{ maxHeight: "calc(100vh - 120px)" }}>
            <div className="row mb-3 border-bottom pb-3">
  <div className="col-md-6 d-flex align-items-center">
    <label className="col-form-label me-2" style={{ width: '40%' }}>Asset Name <span className="text-danger">*</span></label>
    <input className="form-control flex-grow-1" type="text" value={assetName} onChange={(e) => setAssetName(e.target.value)} />
    {errors.assetName && <span className="text-danger">{errors.assetName}</span>}
  </div>
  <div className="col-md-6 d-flex align-items-center">
    <label className="col-form-label me-2" style={{ width: '40%' }}>Asset ID <span className="text-danger">*</span></label>
    <input className="form-control flex-grow-1" type="text" value={assetId} onChange={(e) => setAssetId(e.target.value)} />
    {errors.assetId && <span className="text-danger">{errors.assetId}</span>}
  </div>
</div>

<div className="row mb-3 border-bottom pb-3">
  <div className="col-md-6 d-flex align-items-center">
    <label className="col-form-label me-2" style={{ width: '40%' }}>Purchase Date <span className="text-danger">*</span></label>
    <DatePicker selected={purchaseDate} onChange={(date) => setPurchaseDate(date)} className="form-control flex-grow-1" dateFormat="dd-MM-yyyy" />
    {errors.purchaseDate && <span className="text-danger">{errors.purchaseDate}</span>}
  </div>
  <div className="col-md-6 d-flex align-items-center">
    <label className="col-form-label me-2" style={{ width: '40%' }}>Serial Number <span className="text-danger">*</span></label>
    <input className="form-control flex-grow-1" type="text" value={serialNumber} onChange={(e) => setSerialNumber(e.target.value)} />
    {errors.serialNumber && <span className="text-danger">{errors.serialNumber}</span>}
  </div>
</div>

<div className="row mb-3 border-bottom pb-3">
  <div className="col-md-6 d-flex align-items-center">
    <label className="col-form-label me-2" style={{ width: '40%' }}>Manufacturer <span className="text-danger">*</span></label>
    <input className="form-control flex-grow-1" type="text" value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} />
    {errors.manufacturer && <span className="text-danger">{errors.manufacturer}</span>}
  </div>
  <div className="col-md-6 d-flex align-items-center">
    <label className="col-form-label me-2" style={{ width: '40%' }}>Model <span className="text-danger">*</span></label>
    <input className="form-control flex-grow-1" type="text" value={model} onChange={(e) => setModel(e.target.value)} />
    {errors.model && <span className="text-danger">{errors.model}</span>}
  </div>
</div>

<div className="row mb-3 border-bottom pb-3">
  <div className="col-md-6 d-flex align-items-center">
    <label className="col-form-label me-2" style={{ width: '40%' }}>Warranty (Months) <span className="text-danger">*</span></label>
    <input className="form-control flex-grow-1" type="number" value={warranty} onChange={(e) => setWarranty(e.target.value)} />
    {errors.warranty && <span className="text-danger">{errors.warranty}</span>}
  </div>
  <div className="col-md-6 d-flex align-items-center">
    <label className="col-form-label me-2" style={{ width: '40%' }}>Value ($) <span className="text-danger">*</span></label>
    <input className="form-control flex-grow-1" type="number" value={value} onChange={(e) => setValue(e.target.value)} />
    {errors.value && <span className="text-danger">{errors.value}</span>}
  </div>
</div>

<div className="row mb-3 border-bottom pb-3">
  <div className="col-md-6 d-flex align-items-center">
    <label className="col-form-label me-2" style={{ width: '40%' }}>Condition <span className="text-danger">*</span></label>
    <input className="form-control flex-grow-1" type="text" value={condition} onChange={(e) => setCondition(e.target.value)} />
    {errors.condition && <span className="text-danger">{errors.condition}</span>}
  </div>
  <div className="col-md-6 d-flex align-items-center">
    <label className="col-form-label me-2" style={{ width: '40%' }}>Status</label>
    <Select placeholder="Select Status" value={status} onChange={setStatus} options={statusOptions} className="select floating flex-grow-1" styles={customStyles} />
    {errors.status && <span className="text-danger">{errors.status}</span>}
  </div>
</div>

<div className="row mb-3">
  <div className="col-md-12">
    <label className="col-form-label">Description</label>
    <textarea className="form-control" value={description} onChange={(e) => setDescription(e.target.value)} rows="3" />
  </div>
</div>

            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal" onClick={onClose}>Close</button>
              <button className="btn btn-primary submit-btn" type="submit">{isEditMode ? "Save Changes" : "Submit"}</button>
            </div>
          </form>

          <div style={{ display: "none" }}>
        <button
          ref={submitButtonRef}
          data-bs-dismiss="modal"
          aria-label="Close"
          type="reset"
        >
          Submit2
        </button>
      </div>
        </div>
      </div>
    </div>
  );
};

export default AddEditAssetPopup;
